import React, { useState } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useNotification } from '../../hooks/useNotification';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Calendar,
  User,
  MapPin,
  Package,
  FileText,
  DollarSign,
  Clock,
  Edit,
  Eye,
  Check,
  X,
} from 'lucide-react';
import { Order, OrderStatus } from '../../types';
import CustomerReferenceField from '../../visitors/CustomerReferenceField';
import AddressField from '../../visitors/AddressField';
import NbItemsField from '../NbItemsField';

interface MobileOrderCardProps {
  order: Order;
  onStatusUpdate: (orderId: string, status: OrderStatus) => Promise<void>;
  onEdit: (orderId: string) => void;
  onView: (orderId: string) => void;
  onProductSummaryClick: (order: Order) => void;
  showReturnedColumn: boolean;
}

const statusColors: Record<OrderStatus, string> = {
  ordered: 'bg-blue-100 text-blue-800',
  ready: 'bg-yellow-100 text-yellow-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
};

const statusLabels: Record<OrderStatus, string> = {
  ordered: 'pos.orders.tabs.ordered',
  ready: 'pos.orders.tabs.ready',
  delivered: 'pos.orders.tabs.delivered',
  cancelled: 'pos.orders.tabs.cancelled',
};

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(amount);
};

const calculateOrderTotal = (order: Order): number => {
  if (order.total) {
    return order.total;
  }

  if (order.items && Array.isArray(order.items)) {
    return order.items.reduce((sum, item) => {
      const price = typeof item.price === 'number' ? item.price : 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
      return sum + price * quantity;
    }, 0);
  }

  return 0;
};

const formatDate = (date: Date | string | null | undefined) => {
  if (!date) {
    return '-';
  }

  try {
    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) {
      return '-';
    }

    return new Intl.DateTimeFormat('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', date, error);
    return '-';
  }
};

export const MobileOrderCard: React.FC<MobileOrderCardProps> = ({
  order,
  onStatusUpdate,
  onEdit,
  onView,
  onProductSummaryClick,
  showReturnedColumn,
}) => {
  const { translate } = useTranslation();

  // Check if order can be updated
  const canUpdateOrder =
    !order.paid && order.status !== 'cancelled' && order.status !== 'delivered';

  // Get translated status label
  const getStatusLabel = (status: OrderStatus): string => {
    const translationKey =
      statusLabels[status] || `pos.orders.tabs.${status}`;
    const translated = translate(translationKey);
    // If translation returns the key itself (translation failed), try status translation or fallback
    if (translated === translationKey || translated.startsWith('pos.')) {
      const statusTranslated = translate(`pos.orders.status.${status}`);
      // If status translation also fails, return a default based on status
      if (
        statusTranslated === `pos.orders.status.${status}` ||
        statusTranslated.startsWith('pos.')
      ) {
        // Fallback to hardcoded Vietnamese translations
        const fallbackTranslations: Record<OrderStatus, string> = {
          ordered: 'Đã đặt',
          ready: 'Sẵn sàng',
          delivered: 'Đã giao',
          cancelled: 'Đã hủy',
        };
        return fallbackTranslations[status] || status;
      }
      return statusTranslated;
    }
    return translated;
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 mb-4 shadow-sm">
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-foreground">
              {formatDate(order.date)}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-foreground">
              {order.reference}
            </span>
            <Badge className={statusColors[order.status]}>
              {getStatusLabel(order.status)}
            </Badge>
          </div>
        </div>
        <div className="text-right">
          <div className="font-bold text-green-600 text-lg">
            <span data-sensitive-field="amount">
            {formatCurrency(calculateOrderTotal(order))}
            </span>
          </div>
        </div>
      </div>

      {/* Customer Info */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2">
          <User className="h-4 w-4 text-gray-500" />
          <div className="flex-1">
            {order.customerId ? (
              <CustomerReferenceField
                customerId={order.customerId}
                showPoints={true}
                showZalo={true}
                sourcePlatform={(order as any).sourcePlatform}
              />
            ) : (order as any).sourcePlatform &&
              (order as any).sourcePlatform !== 'internal' ? (
              <Badge
                variant="outline"
                className="bg-green-50 text-green-700 border-green-200"
              >
                {(() => {
                  const platform = (order as any).sourcePlatform?.toLowerCase();
                  const platformMap: Record<string, string> = {
                    grab: 'Grab',
                    shopee: 'Shopee',
                    xanhsm: 'Xanh SM',
                    xanh_sm: 'Xanh SM',
                  };
                  return platformMap[platform] || platform;
                })()}
              </Badge>
            ) : (
              <span className="text-muted-foreground italic">
                {translate('pos.orders.modern.walkInCustomer')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-start gap-2">
          <MapPin className="h-4 w-4 text-gray-500 mt-0.5" />
          <div className="flex-1">
            {(() => {
              // Check order type first to determine display
              const orderType = order.orderType?.toLowerCase();

              if (orderType === 'delivery') {
                // For delivery orders, show customer address if available
                if (order.customerId) {
                  return <AddressField customerId={order.customerId} />;
                }
                // If no customer, show "Delivery"
                return (
                  <span className="text-muted-foreground italic">
                    {translate('pos.orders.orderTypes.delivery')}
                  </span>
                );
              }

              if (orderType === 'takeaway') {
                // For takeaway orders, show "Takeaway"
                return (
                  <span className="text-muted-foreground italic">
                    {translate('pos.orders.orderTypes.takeaway')}
                  </span>
                );
              }

              // For dine-in orders (default or explicit)
              if (order.customerId) {
                // If customer exists, show their address
                return <AddressField customerId={order.customerId} />;
              }

              // Default to dine-in display with table number if available
              return (
                <span className="text-muted-foreground italic">
                  {order.tableNumber
                    ? translate('pos.orders.modern.dineInTable', {
                        tableNumber: String(order.tableNumber),
                      })
                    : translate('pos.orders.modern.dineIn')}
                </span>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Items and Notes */}
      <div className="space-y-2 mb-3">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-gray-500" />
          <div className="flex-1">
            {order.items && Array.isArray(order.items) ? (
              <button
                onClick={() => onProductSummaryClick(order)}
                className="font-medium text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
              >
                {order.items.reduce(
                  (sum, item) => sum + (item.quantity || 0),
                  0
                )}{' '}
                {translate('pos.orders.modern.items')}
              </button>
            ) : (
              <NbItemsField />
            )}
          </div>
        </div>

        <div className="flex items-start gap-2">
          <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
          <div className="flex-1">
            {(() => {
              const itemNotes = order.items
                ?.filter((item) => item.note)
                .map((item) => item.note)
                .filter(Boolean);

              if (itemNotes && itemNotes.length > 0) {
                return (
                  <div className="space-y-1">
                    {itemNotes.map((note, index) => (
                      <div
                        key={index}
                        className="text-muted-foreground text-xs truncate"
                        title={note}
                      >
                        • {note}
                      </div>
                    ))}
                  </div>
                );
              } else {
                return (
                  <span className="text-muted-foreground italic text-sm">
                    {translate('pos.orders.modern.noNotes')}
                  </span>
                );
              }
            })()}
          </div>
        </div>
      </div>

      {/* Returned Status */}
      {showReturnedColumn && order.returned && (
        <div className="mb-3">
          <Badge variant="destructive" className="bg-red-100 text-red-800">
            {translate('pos.orders.modern.returned')}
          </Badge>
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-col gap-3">
        {/* Status Update - Mobile Friendly */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-foreground">
              {translate('pos.orders.modern.actions.updateStatus')}
            </span>
          </div>

          {/* Status Buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant={order.status === 'ordered' ? 'default' : 'outline'}
              size="sm"
              className={`h-12 text-sm font-medium ${
                order.status === 'ordered'
                  ? 'bg-blue-600 text-white'
                  : 'border-border text-foreground hover:bg-blue-50 dark:hover:bg-blue-900'
              }`}
              onClick={() => onStatusUpdate(order.id.toString(), 'ordered')}
              disabled={!canUpdateOrder || order.status === 'ordered'}
            >
              {order.status === 'ordered' && <Check className="h-4 w-4 mr-1" />}
              {translate('pos.orders.tabs.ordered')}
            </Button>

            <Button
              variant={order.status === 'ready' ? 'default' : 'outline'}
              size="sm"
              className={`h-12 text-sm font-medium ${
                order.status === 'ready'
                  ? 'bg-yellow-600 text-white'
                  : 'border-border text-foreground hover:bg-yellow-50 dark:hover:bg-yellow-900'
              }`}
              onClick={() => onStatusUpdate(order.id.toString(), 'ready')}
              disabled={!canUpdateOrder}
            >
              {order.status === 'ready' && <Check className="h-4 w-4 mr-1" />}
              {translate('pos.orders.tabs.ready')}
            </Button>

            <Button
              variant={order.status === 'delivered' ? 'default' : 'outline'}
              size="sm"
              className={`h-12 text-sm font-medium ${
                order.status === 'delivered'
                  ? 'bg-green-600 text-white'
                  : 'border-border text-foreground hover:bg-green-50 dark:hover:bg-green-900'
              }`}
              onClick={() => onStatusUpdate(order.id.toString(), 'delivered')}
              disabled={!canUpdateOrder}
            >
              {order.status === 'delivered' && (
                <Check className="h-4 w-4 mr-1" />
              )}
              {translate('pos.orders.tabs.delivered')}
            </Button>

            <Button
              variant={order.status === 'cancelled' ? 'default' : 'outline'}
              size="sm"
              className={`h-12 text-sm font-medium ${
                order.status === 'cancelled'
                  ? 'bg-red-600 text-white'
                  : 'border-border text-foreground hover:bg-red-50 dark:hover:bg-red-900'
              }`}
              onClick={() => onStatusUpdate(order.id.toString(), 'cancelled')}
              disabled={!canUpdateOrder}
            >
              {order.status === 'cancelled' && <X className="h-4 w-4 mr-1" />}
              {translate('pos.orders.tabs.cancelled')}
            </Button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10"
            onClick={() => onEdit(order.id.toString())}
          >
            <Edit className="h-4 w-4 mr-1" />
            {translate('pos.common.action.edit')}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="flex-1 h-10"
            onClick={() => onView(order.id.toString())}
          >
            <Eye className="h-4 w-4 mr-1" />
            {translate('pos.common.action.show')}
          </Button>
        </div>
      </div>
    </div>
  );
};
